import { useCallback } from 'react';
import { useSafeIsFocused } from '@sg/garnish';

import { useLocalizationContext } from '@order/Localization';

import { useChallengeCompletionModal } from '../../../../components';
import {
  checkIfLateOrder,
  checkIfPastOrder,
} from '../../../../context/OrderStatus';
import { useUserLocation } from '../../../../hooks';
import { useReorderFromOrder } from '../../../ReorderingScreen';
import { useMomoSurveyUrl } from '../useMomoSurveyUrl';
import { useOrderHealthKit } from '../useOrderHealthKit';
import { useOrderStatusPolling } from '../useOrderStatusPolling';
import { useOrderStatusRequest } from '../useOrderStatusRequest';

export function useOrderStatusData() {
  const isRenderedOnScreen = useSafeIsFocused();

  // ─── Request ─────────────────────────────────────────────────────────

  const response = useOrderStatusRequest();

  const {
    order,
    courierDetails,
    orderStatus,
    orderId,
    orderType,
    isInStoreOrder,
    isFetching: isFetchingOrderStatus,
    refetchCanCancelStatus,
  } = response;

  // ─── Order Status Polling ────────────────────────────────────────────

  const {
    status,
    courier,
    refetchOrderStatus: refetchOrderStatusData,
  } = useOrderStatusPolling(orderId, orderType, orderStatus, courierDetails);

  const refetchOrderStatus = useCallback(() => {
    refetchCanCancelStatus();
    refetchOrderStatusData();
  }, [refetchCanCancelStatus, refetchOrderStatusData]);

  // ─── Derived Statuses ────────────────────────────────────────────────

  const isLate = checkIfLateOrder({ order, flattenedOrderStatus: status });
  const isPast = checkIfPastOrder({ order, flattenedOrderStatus: status });

  // ─── Health Kit ──────────────────────────────────────────────────────

  const { isOrderToHealthKitEnabled, orderToHealthKitLineItems } =
    useOrderHealthKit(order);

  // ─── User Data ───────────────────────────────────────────────────────

  const customerName = useLocalizationContext().t('general.you');
  const { geolocation } = useUserLocation({
    pollingInterval: GEO_POLLING_INTERVAL,
  });
  const { latitude: userLat, longitude: userLon } = geolocation ?? {};

  // ─── Reorder ─────────────────────────────────────────────────────────

  const onReorder = useReorderFromOrder(order);

  // ─── Momo Survey ─────────────────────────────────────────────────────

  const momoSurveyUrl = useMomoSurveyUrl({
    isInStoreOrder,
    orderId,
    orderTime: order?.wantedTime ?? '',
    restaurantEntityId: String(order?.restaurant?.entityId ?? ''),
  });

  // ─── Challenge Celebration ───────────────────────────────────────────

  const { celebration, fetchingCelebration: isFetchingCelebration } =
    useChallengeCompletionModal(orderId);

  // ─── Data ────────────────────────────────────────────────────────────

  return {
    ...response,
    isFetching: isFetchingOrderStatus || isFetchingCelebration,

    isRenderedOnScreen,

    status,

    orderToHealthKitLineItems,
    isOrderToHealthKitEnabled,

    isLate,
    isPast,

    customerName,
    customerLatitude: userLat ?? 0,
    customerLongitude: userLon ?? 0,

    courierName: courier?.name,
    courierPhone: courier?.phone,
    courierLatitude: courier?.latitude,
    courierLongitude: courier?.longitude,

    momoSurveyUrl,
    celebration,

    onReorder,
    refetchOrderStatus,
  };
}

// ─── Constants ─────────────────────────────────────────────────────────

const GEO_POLLING_INTERVAL = 60 * 1000; // 60 seconds.
